import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, MapPin, Loader2, Sparkles, Car, ShieldCheck, Star, Share2, Download, ArrowRight, TrendingDown } from "lucide-react";
import { publicService, serviceMarketing, serviceReview, mediaUrl } from "@/lib/api";
import { MODULES, MEMBERSHIPS, fmt } from "@/data/autosCatalog";

const GOLD = "#C7A668";
const WHATSAPP_NUMBER = "525578988731";

function sizeMult(label) {
  const l = (label || "").toLowerCase();
  if (l.includes("grande") || l.includes("3")) return 1.30;
  if (l.includes("suv") || l.includes("camioneta") || l.includes("pickup") || l.includes("truck")) return 1.15;
  return 1.0;
}

function BeforeAfter({ before, after }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const move = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  useEffect(() => {
    const up = () => { dragging.current = false; };
    const mv = (e) => { if (dragging.current) move(e.touches ? e.touches[0].clientX : e.clientX); };
    window.addEventListener("mousemove", mv);
    window.addEventListener("touchmove", mv, { passive: true });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => { window.removeEventListener("mousemove", mv); window.removeEventListener("touchmove", mv); window.removeEventListener("mouseup", up); window.removeEventListener("touchend", up); };
  }, [move]);

  return (
    <div ref={ref} data-testid="before-after-slider" className="relative w-full select-none rounded-sm overflow-hidden" style={{ aspectRatio: "4/3", background: "#000", cursor: "ew-resize" }}
      onMouseDown={(e) => { dragging.current = true; move(e.clientX); }}
      onTouchStart={(e) => { dragging.current = true; move(e.touches[0].clientX); }}>
      <img src={after} alt="después" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      <div className="absolute inset-0 h-full overflow-hidden" style={{ width: `${pos}%` }}>
        <img src={before} alt="antes" className="absolute inset-0 h-full object-cover" style={{ width: ref.current ? ref.current.getBoundingClientRect().width : "100%", maxWidth: "none" }} draggable={false} />
      </div>
      <div className="absolute top-0 bottom-0" style={{ left: `calc(${pos}% - 1px)`, width: 2, background: GOLD, boxShadow: "0 0 12px rgba(199,166,104,.7)" }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center" style={{ width: 34, height: 34, background: GOLD, color: "#0a0a0a", fontSize: 11, fontWeight: 700 }}>↔</div>
      </div>
      <span className="absolute top-3 left-3 font-mono text-[10px] tracking-widest uppercase px-2 py-1 rounded-sm" style={{ background: "rgba(0,0,0,.6)", color: "#fff" }}>Antes</span>
      <span className="absolute top-3 right-3 font-mono text-[10px] tracking-widest uppercase px-2 py-1 rounded-sm" style={{ background: "rgba(199,166,104,.85)", color: "#0a0a0a" }}>Después</span>
    </div>
  );
}

export default function ServiceTracker() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [optBusy, setOptBusy] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverR, setHoverR] = useState(0);
  const [comment, setComment] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewDone, setReviewDone] = useState(false);
  const [shareMsg, setShareMsg] = useState("");

  const load = useCallback(async () => {
    try { const d = await publicService(token); setData(d); }
    catch (e) { setError(e?.response?.status === 404 ? "notfound" : "error"); }
  }, [token]);

  useEffect(() => {
    load();
    const iv = setInterval(() => { if (document.visibilityState === "visible") load(); }, 20000);
    document.title = "Seguimiento de servicio | Dukk";
    return () => clearInterval(iv);
  }, [load]);

  if (error === "notfound") return (
    <div className="grain min-h-screen bg-[#0A0A0B] text-[#F2EFE8] flex flex-col items-center justify-center px-6 text-center">
      <div className="font-display text-3xl" style={{ fontWeight: 300 }}>Servicio no encontrado</div>
      <p className="text-white/45 mt-3">Verifica el enlace que te compartimos por WhatsApp.</p>
      <Link to="/autos" className="mt-6 font-mono text-xs" style={{ color: GOLD }}>← Volver a Dukk Autos</Link>
    </div>
  );

  if (!data) return (
    <div className="grain min-h-screen bg-[#0A0A0B] flex items-center justify-center"><Loader2 className="animate-spin" color={GOLD} /></div>
  );

  const { service: s, states, requiredPairs } = data;
  const completed = s.state >= 7;

  // group media into pairs
  const pairsMap = {};
  (s.media || []).forEach((m) => { pairsMap[m.pair] = pairsMap[m.pair] || {}; pairsMap[m.pair][m.kind] = m; });
  const pairs = Object.keys(pairsMap).map(Number).sort((a, b) => a - b)
    .map((p) => ({ pair: p, before: pairsMap[p].before, after: pairsMap[p].after }))
    .filter((p) => p.before && p.after);

  const toggleOpt = async () => {
    setOptBusy(true);
    try { const r = await serviceMarketing(token, !s.marketingOptIn); setData({ ...data, service: { ...s, marketingOptIn: r.marketingOptIn } }); } catch (_) {}
    setOptBusy(false);
  };

  // ---- Membership savings (what a member would have saved on this service) ----
  const mult = sizeMult(s.sizeLabel);
  const moduleSubtotal = Math.round((s.modules || []).reduce((sum, k) => {
    const m = MODULES.find((x) => x.key === k);
    return sum + (m && m.price ? m.price : 0);
  }, 0) * mult);
  const plan = MEMBERSHIPS.find((m) => m.key === "completa");
  const moduleSaving = Math.round(moduleSubtotal * 0.20);
  const includedWashesValue = 2400; // 4 Salidas / mes ($600 c/u)

  // ---- Review already submitted? ----
  const alreadyReviewed = !!s.rating && !reviewDone;
  const shownRating = s.rating || rating;

  const submitReview = async () => {
    if (!rating) return;
    setReviewBusy(true);
    try { await serviceReview(token, rating, comment); setReviewDone(true); setData({ ...data, service: { ...s, rating, reviewComment: comment } }); } catch (_) {}
    setReviewBusy(false);
  };

  const shareText = `Mira cómo quedó mi ${[s.carMake, s.carModel].filter(Boolean).join(" ") || "auto"} con Dukk ✨ Lavado y detailing premium a domicilio.`;
  const shareUrl = `${window.location.origin}/servicio/${token}`;
  const doShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Mi servicio Dukk", text: shareText, url: shareUrl }); return; } catch (_) {}
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, "_blank");
  };
  const downloadAfter = async () => {
    if (!pairs.length) return;
    try {
      setShareMsg("Descargando…");
      const res = await fetch(mediaUrl(pairs[0].after.path));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `dukk-${s.serviceId}-despues.jpg`; a.click();
      URL.revokeObjectURL(url); setShareMsg("");
    } catch (_) { setShareMsg("No se pudo descargar"); }
  };

  const waHelp = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hola Dukk, sobre mi servicio ${s.serviceId}`)}`;
  const joinWa = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hola Dukk, me interesa la membresía ${plan ? plan.es : "Completa"}.`)}`;

  return (
    <div className="grain min-h-screen bg-[#0A0A0B] text-[#F2EFE8] px-6 pt-20 pb-24">
      <Link to="/autos" className="no-underline font-display" style={{ position: "fixed", top: 20, left: 24, color: "#F2EFE8", fontSize: 20, letterSpacing: ".12em", fontWeight: 300, zIndex: 30 }}>DUKK</Link>
      <div className="max-w-2xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="font-mono text-xs tracking-widest uppercase" style={{ color: GOLD }}>Seguimiento de servicio</div>
          <h1 className="font-display text-3xl md:text-4xl text-white mt-3" style={{ fontWeight: 300 }}>
            <Car size={26} className="inline mr-2 -mt-1" color={GOLD} />
            {[s.carMake, s.carModel].filter(Boolean).join(" ") || "Tu auto"}
          </h1>
          <div className="text-white/45 text-sm mt-2">
            {[s.sizeLabel, s.carColor].filter(Boolean).join(" · ")} {s.packageName ? `· ${s.packageName}` : ""} · <span className="font-mono">{s.serviceId}</span>
          </div>
        </motion.div>

        {/* Timeline */}
        <div className="mt-10" data-testid="tracker-timeline">
          {states.map((st, i) => {
            const isCurrent = st.n === s.state;
            const reached = st.reached;
            return (
              <div key={st.key} className="flex gap-4" data-testid={`tracker-state-${st.n}`}>
                <div className="flex flex-col items-center">
                  <div className="rounded-full flex items-center justify-center transition-all" style={{ width: 30, height: 30, flex: "none", border: `1.5px solid ${reached ? GOLD : "rgba(255,255,255,.18)"}`, background: reached ? GOLD : "transparent", boxShadow: isCurrent ? `0 0 16px ${GOLD}` : "none" }}>
                    {reached ? <Check size={15} color="#0a0a0a" strokeWidth={3} /> : <span className="font-mono text-xs text-white/40">{st.n}</span>}
                  </div>
                  {i < states.length - 1 && <div style={{ width: 1.5, flex: 1, minHeight: 34, background: reached ? GOLD : "rgba(255,255,255,.12)" }} />}
                </div>
                <div className={`pb-7 ${isCurrent ? "" : "opacity-80"}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-display text-lg text-white" style={{ fontWeight: isCurrent ? 400 : 300 }}>{st.es}</span>
                    {isCurrent && <span className="font-mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full" style={{ background: "rgba(199,166,104,.15)", color: GOLD }}>Ahora</span>}
                  </div>
                  {reached && st.message && <p className="text-white/55 text-sm mt-1 leading-relaxed">{st.message}</p>}
                  {isCurrent && st.asksLocation && (
                    <a href={waHelp} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-3 font-mono text-xs no-underline" style={{ color: "#0a0a0a", background: GOLD, borderRadius: 999, padding: "8px 16px", fontWeight: 600 }} data-testid="tracker-share-location">
                      <MapPin size={13} /> Compartir mi ubicación por WhatsApp
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Report / before-after when completed */}
        {completed && pairs.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-6" data-testid="tracker-report">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={18} color={GOLD} />
              <h2 className="font-display text-2xl text-white" style={{ fontWeight: 300 }}>Reporte del servicio</h2>
            </div>
            <p className="text-white/45 text-sm mb-5">{pairs.length} zonas documentadas · arrastra para comparar antes / después.</p>

            {/* Featured slider (first pair) */}
            <BeforeAfter before={mediaUrl(pairs[0].before.path)} after={mediaUrl(pairs[0].after.path)} />

            {/* Grid of all pairs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5" data-testid="tracker-report-grid">
              {pairs.map((p) => (
                <div key={p.pair} className="rounded-sm overflow-hidden" style={{ border: "1px solid rgba(199,166,104,.2)" }}>
                  <div className="grid grid-cols-2">
                    <div className="relative"><img src={mediaUrl(p.before.path)} alt="antes" className="w-full h-full object-cover" style={{ aspectRatio: "1/1" }} /><span className="absolute bottom-1 left-1 font-mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-sm" style={{ background: "rgba(0,0,0,.6)", color: "#fff" }}>Antes</span></div>
                    <div className="relative"><img src={mediaUrl(p.after.path)} alt="después" className="w-full h-full object-cover" style={{ aspectRatio: "1/1" }} /><span className="absolute bottom-1 right-1 font-mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-sm" style={{ background: "rgba(199,166,104,.85)", color: "#0a0a0a" }}>Después</span></div>
                  </div>
                </div>
              ))}
            </div>

            {s.techNotes && (
              <div className="mt-5 rounded-sm p-4" style={{ background: "rgba(199,166,104,.06)", border: "1px solid rgba(199,166,104,.15)" }}>
                <div className="font-mono text-[10px] tracking-widest uppercase text-white/40 mb-1">Notas del técnico</div>
                <p className="text-white/70 text-sm leading-relaxed">{s.techNotes}</p>
              </div>
            )}

            {(s.checklist || []).length > 0 && (
              <div className="mt-5">
                <div className="font-mono text-[10px] tracking-widest uppercase text-white/40 mb-2">Checklist del servicio base</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {s.checklist.map((c, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-white/65"><Check size={14} color={GOLD} strokeWidth={2.5} className="mt-0.5 flex-none" />{c.item}</div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Membership savings reminder */}
        {completed && plan && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-8 rounded-sm p-6" data-testid="tracker-membership-savings" style={{ background: "linear-gradient(160deg,#16130c,#0a0a0a 60%)", border: `1px solid ${GOLD}` }}>
            <div className="flex items-center gap-2">
              <TrendingDown size={18} color={GOLD} />
              <h3 className="font-display text-2xl text-white" style={{ fontWeight: 300 }}>¿Y si fueras miembro?</h3>
            </div>
            {moduleSaving > 0 ? (
              <p className="text-white/60 text-sm mt-3 leading-relaxed">
                Con la membresía <b style={{ color: GOLD }}>{plan.es}</b> ({fmt(plan.price)}/mes) tus módulos llevan <b>20% de descuento</b>. En un servicio como este habrías ahorrado <b style={{ color: GOLD }} data-testid="tracker-saving-amount">{fmt(moduleSaving)}</b> — y además tendrías <b>4 lavados La Salida al mes incluidos</b> (valor {fmt(includedWashesValue)}).
              </p>
            ) : (
              <p className="text-white/60 text-sm mt-3 leading-relaxed">
                Con la membresía <b style={{ color: GOLD }}>{plan.es}</b> ({fmt(plan.price)}/mes) tendrías <b>4 lavados La Salida al mes incluidos</b> (valor <b style={{ color: GOLD }} data-testid="tracker-saving-amount">{fmt(includedWashesValue)}</b>), agenda prioritaria y 20% en todos los módulos.
              </p>
            )}
            <div className="flex flex-wrap gap-3 mt-5">
              <Link to="/autos/membresias" data-testid="tracker-see-memberships" className="inline-flex items-center gap-2 no-underline font-mono text-xs" style={{ color: "#0a0a0a", background: GOLD, borderRadius: 999, padding: "11px 22px", fontWeight: 600 }}>Ver membresías <ArrowRight size={14} /></Link>
              <a href={joinWa} target="_blank" rel="noreferrer" data-testid="tracker-join-wa" className="inline-flex items-center gap-2 no-underline font-mono text-xs" style={{ color: GOLD, border: `1px solid ${GOLD}`, borderRadius: 999, padding: "11px 22px" }}>Hazte miembro</a>
            </div>
          </motion.div>
        )}

        {/* Review + share */}
        {completed && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-sm p-6" data-testid="tracker-review" style={{ background: "#121214", border: "1px solid rgba(255,255,255,.1)" }}>
            {alreadyReviewed || reviewDone ? (
              <div data-testid="tracker-review-done">
                <div className="font-display text-xl text-white" style={{ fontWeight: 300 }}>¡Gracias por tu calificación!</div>
                <div className="flex gap-1 mt-3">
                  {[1, 2, 3, 4, 5].map((n) => (<Star key={n} size={22} color={GOLD} fill={n <= shownRating ? GOLD : "transparent"} />))}
                </div>
                {(s.reviewComment || comment) && <p className="text-white/50 text-sm mt-3 italic">"{s.reviewComment || comment}"</p>}
              </div>
            ) : (
              <>
                <div className="font-display text-xl text-white" style={{ fontWeight: 300 }}>¿Cómo quedó tu auto?</div>
                <p className="text-white/45 text-sm mt-1">Tu opinión nos ayuda a cuidar mejor cada auto.</p>
                <div className="flex gap-1.5 mt-4">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} data-testid={`tracker-star-${n}`} onMouseEnter={() => setHoverR(n)} onMouseLeave={() => setHoverR(0)} onClick={() => setRating(n)} style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}>
                      <Star size={30} color={GOLD} fill={n <= (hoverR || rating) ? GOLD : "transparent"} strokeWidth={1.5} />
                    </button>
                  ))}
                </div>
                <textarea data-testid="tracker-review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Cuéntanos algo (opcional)…" className="w-full mt-4 rounded-sm px-3 py-2 text-white text-sm outline-none" style={{ background: "#0d0d0f", border: "1px solid rgba(255,255,255,.14)" }} />
                <button onClick={submitReview} disabled={!rating || reviewBusy} data-testid="tracker-review-submit" className="mt-4 rounded-sm px-6 py-3 font-semibold" style={{ background: rating ? GOLD : "rgba(199,166,104,.25)", color: rating ? "#0a0a0a" : "rgba(255,255,255,.4)", border: "none", cursor: rating ? "pointer" : "not-allowed" }}>{reviewBusy ? "…" : "Enviar calificación"}</button>
              </>
            )}

            {/* Share */}
            <div className="mt-6 pt-6" style={{ borderTop: "1px solid rgba(255,255,255,.08)" }}>
              <div className="font-mono text-[10px] tracking-widest uppercase text-white/40 mb-3">Comparte tu antes / después</div>
              <div className="flex flex-wrap gap-3">
                <button onClick={doShare} data-testid="tracker-share-btn" className="inline-flex items-center gap-2 font-mono text-xs rounded-full" style={{ color: "#0a0a0a", background: "#F2EFE8", padding: "10px 20px", border: "none", cursor: "pointer", fontWeight: 600 }}><Share2 size={14} /> Compartir</button>
                {pairs.length > 0 && <button onClick={downloadAfter} data-testid="tracker-download-btn" className="inline-flex items-center gap-2 font-mono text-xs rounded-full" style={{ color: GOLD, background: "transparent", padding: "10px 20px", border: `1px solid ${GOLD}`, cursor: "pointer" }}><Download size={14} /> Descargar foto</button>}
              </div>
              {shareMsg && <p className="text-white/40 text-xs mt-2">{shareMsg}</p>}
            </div>
          </motion.div>
        )}

        {completed && (
          <button onClick={toggleOpt} disabled={optBusy} data-testid="tracker-marketing-opt" className="w-full mt-8 rounded-sm p-4 flex items-center gap-3 text-left transition-colors" style={{ border: `1px solid ${s.marketingOptIn ? GOLD : "rgba(255,255,255,.12)"}`, background: s.marketingOptIn ? "rgba(199,166,104,.08)" : "#101012", cursor: "pointer" }}>
            <div className="rounded-sm flex items-center justify-center flex-none" style={{ width: 22, height: 22, border: `1.5px solid ${s.marketingOptIn ? GOLD : "rgba(255,255,255,.3)"}`, background: s.marketingOptIn ? GOLD : "transparent" }}>{s.marketingOptIn && <Check size={13} color="#0a0a0a" strokeWidth={3} />}</div>
            <span className="text-sm text-white/70"><Sparkles size={13} className="inline mr-1" color={GOLD} /> Autorizo a Dukk usar el antes/después de mi auto en redes (sin datos personales ni placas).</span>
          </button>
        )}

        <a href={waHelp} target="_blank" rel="noreferrer" className="block text-center mt-6 font-mono text-xs no-underline" style={{ color: GOLD }} data-testid="tracker-whatsapp">¿Dudas? Escríbenos por WhatsApp →</a>
      </div>
    </div>
  );
}
